import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchWishlist,
  addToWishlist,
  removeFromWishlist,
  checkIfWishlisted,
} from "@/services/wishlistService"; // 👈 uses the Supabase + backend fallback

// ==========================================================
// 🔄 Thunks
// ==========================================================

// ✅ Load Wishlist (backend or Supabase fallback)
export const loadWishlist = createAsyncThunk(
  "wishlist/load",
  async (_, { rejectWithValue }) => {
    try {
      const items = await fetchWishlist();
      return items || [];
    } catch (err) {
      console.error("❌ loadWishlist failed:", err.message);
      return rejectWithValue(err.message);
    }
  }
);

// ✅ Add Item
export const addWishlistItem = createAsyncThunk(
  "wishlist/add",
  async (productId, { rejectWithValue }) => {
    try {
      await addToWishlist(productId);
      const updatedList = await fetchWishlist();
      return updatedList || [];
    } catch (err) {
      console.error("❌ addWishlistItem failed:", err.message);
      return rejectWithValue(err.message);
    }
  }
);

// ✅ Remove Item
export const removeWishlistItem = createAsyncThunk(
  "wishlist/remove",
  async (productId, { rejectWithValue }) => {
    try {
      await removeFromWishlist(productId);
      const updatedList = await fetchWishlist();
      return updatedList || [];
    } catch (err) {
      console.error("❌ removeWishlistItem failed:", err.message);
      return rejectWithValue(err.message);
    }
  }
);

// ✅ Check if item is wishlisted (for heart icon states)
export const checkWishlistStatus = createAsyncThunk(
  "wishlist/checkStatus",
  async (productId, { rejectWithValue }) => {
    try {
      return await checkIfWishlisted(productId);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ==========================================================
// 🧩 Slice
// ==========================================================
const wishlistSlice = createSlice({
  name: "wishlist",
  initialState: {
    items: [],
    loading: false,
    error: null,
    isWishlisted: {}, // ✅ store per-product status
  },
  reducers: {
    // Local clear (e.g. on logout)
    clearWishlistLocal: (state) => {
      state.items = [];
      state.loading = false;
      state.error = null;
      state.isWishlisted = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // 🟡 Load wishlist
      .addCase(loadWishlist.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload || [];
      })
      .addCase(loadWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // 🟢 Add item
      .addCase(addWishlistItem.fulfilled, (state, action) => {
        state.items = action.payload || [];
        state.error = null;
      })

      // 🔴 Remove item
      .addCase(removeWishlistItem.fulfilled, (state, action) => {
        state.items = action.payload || [];
        state.error = null;
      })

      // 💬 Check wishlist status
      .addCase(checkWishlistStatus.fulfilled, (state, action) => {
        const productId = action.meta.arg;
        state.isWishlisted[productId] = action.payload;
      });
  },
});

export const { clearWishlistLocal } = wishlistSlice.actions;
export default wishlistSlice.reducer;
